import { type AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuthStore } from "@store/auth/auth";
import { useModalStore } from "@store/modal/modal";
import { useStatisticsStore } from "@store/statistics/statistics";
import { type IImage } from "@services/additional-services/types";
import { type IFile } from "@services/jointBillServices/types";
import { IS_APP_KYC_MODE } from "@/constants";
import { type IApiErrorResponse } from "@/types";

export function isNullOrUndefined<T>(
  obj: T | null | undefined,
): obj is null | undefined {
  return typeof obj === "undefined" || obj === null;
}

export const formatImageSrc = (image: IImage) =>
  `data:${image.mimeType};base64,${image.contentBase64}`;

export const encodeBase64 = (text: string): string =>
  btoa(String.fromCodePoint(...new TextEncoder().encode(text)));

export const decodeBase64 = (text?: string): string => {
  if (!text) return "";

  return new TextDecoder().decode(
    // @ts-expect-error - c
    Uint8Array.from(atob(text), (m) => m.codePointAt(0)),
  );
};

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

export const saveByteArrayAsPdf = (
  fileName: string,
  byte: Uint8Array,
  contentType = "application/pdf",
) => {
  const blob = new Blob([byte], { type: contentType });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const openPdfPreview = (fileName: string, base64: string) => {
  const file = new File([base64ToArrayBuffer(base64)], fileName, {
    type: "application/pdf",
  });

  openFileInNewTab(file);
};

export const convertFileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      resolve(reader.result as string);
    };
    reader.onerror = function (error) {
      reject(error);
    };
  });

export const getAxiosErrorData = (error: AxiosError): IApiErrorResponse =>
  error?.response?.data as IApiErrorResponse;

export const copyToClipboard = (
  value?: string | number,
  successMsg?: string,
  errorMsg?: string,
) => {
  navigator.clipboard
    .writeText(`${value}`)
    .then(() => {
      if (successMsg) {
        toast.success(successMsg);
      }
    })
    .catch(() => {
      if (errorMsg) {
        toast.success(errorMsg);
      }
    });
};

export const downloadFile = (file: IFile, contentType = "application/pdf") => {
  if (IS_APP_KYC_MODE && Download) {
    Download.postMessage(
      JSON.stringify({
        fileName: file.fileName,
        fileData: file.data,
      }),
    );

    return;
  }

  saveByteArrayAsPdf(
    file.fileName,
    base64ToArrayBuffer(file.data),
    contentType,
  );
};

export const openFileInNewTab = (file: File) => {
  const fileURL = URL.createObjectURL(file);
  window.open(fileURL);
};

export const clearUserState = () => {
  const { clearToken } = useAuthStore.getState();
  const { clear: clearModals } = useModalStore.getState();
  const { clear: clearStatistics } = useStatisticsStore.getState();

  clearToken();
  clearModals();
  clearStatistics();
};

export const convertYearMonthToDate = ({
  year,
  month,
}: {
  year?: number;
  month?: number;
}) => new Date(`${year}-${month?.toString().padStart(2, "0")}`);

export const getCountryName = (countryCode = "", locale: string) => {
  try {
    const regionNames = new Intl.DisplayNames([locale], { type: "region" });
    return regionNames.of(countryCode);
  } catch {
    return undefined;
  }
};

export const scrollToElement = (element?: HTMLElement) => {
  const yOffset = -250;

  if (element) {
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

export const separateThousands = (x: number, separator = ",") =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.replace(/^data:.*;base64,/, ""));
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });

// @ts-expect-error - c
export const parseTitleAttribute = (value): string => {
  switch (typeof value) {
    case "string":
      return value;
    case "number":
      return String(value);
    default:
      return "";
  }
};

export const useCurrentLanguageCode = () => {
  const { i18n } = useTranslation();
  return i18n.language === "ee" ? "et" : i18n.language;
};
